import { Link } from "react-router-dom";
import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Tooltip,
} from "@heroui/react";
import { LuPencil, LuTrash, LuPlus, LuEgg, LuArrowRight } from "react-icons/lu";
import { useDataTable } from "@/hooks";
import {
    DataTableControlBar,
    DataTableSkeleton,
    DataTablePagination,
    DeleteConfirmModal,
    FilterButton,
    ActiveFilterChips,
} from "@/components/data-table";
import {
    EmptyState,
    ErrorState,
    ThemedText,
    PageHeader,
} from "@/components/ui";
import { formatThousands, formatDate, formatCurrency } from "@/utils";
import { FilterData } from "./components";
import { useUser } from "@/context/UserContext";

const initialFilters = {
    warehouse_id: "",
    date_from: "",
    date_to: "",
};

const EggConversionData = () => {
    const { hasPermission } = useUser();
    const hasAnyAction =
        hasPermission("egg-conversion.edit") ||
        hasPermission("egg-conversion.delete");

    const table = useDataTable(
        "egg-conversions/data",
        "egg-conversions/{id}/delete",
        {},
        initialFilters,
    );

    const filterLabels = {
        warehouse_id: { label: "Gudang" },
        date_from: {
            label: "Dari Tanggal",
            getValueLabel: (v) => formatDate(v),
        },
        date_to: {
            label: "Sampai Tanggal",
            getValueLabel: (v) => formatDate(v),
        },
    };

    const handleRemoveFilter = (key) => {
        if (key === "date_from" || key === "date_to") {
            table.filterPanel.setPending((prev) => ({
                ...prev,
                date_from: "",
                date_to: "",
            }));
            table.filters.setFilter("date_from", "");
            table.filters.setFilter("date_to", "");
        } else {
            table.filterPanel.setPending((prev) => ({
                ...prev,
                [key]: initialFilters[key],
            }));
            table.filters.setFilter(key, initialFilters[key]);
        }
    };

    if (table.error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Data"
                    description="Terjadi kesalahan saat memuat data konversi telur."
                    onAction={table.refetch}
                />
            </div>
        );
    }

    const skeletonColumns = [
        { name: "No.", skeletonWidth: "w-8" },
        { name: "Tanggal", skeletonWidth: "w-24" },
        { name: "Konversi", skeletonWidth: "w-48" },
        { name: "Gudang", skeletonWidth: "w-28" },
        { name: "Jumlah", skeletonWidth: "w-20", align: "end" },
        { name: "@HPP", skeletonWidth: "w-24", align: "end" },
        { name: "Total Nilai", skeletonWidth: "w-28", align: "end" },
        ...(hasAnyAction
            ? [{ name: "Aksi", skeletonWidth: "w-20", align: "end" }]
            : []),
    ];

    const columns = [
        { key: "no", label: "No.", width: 50 },
        { key: "date", label: "Tanggal" },
        { key: "conversion", label: "Konversi" },
        { key: "warehouse", label: "Gudang" },
        { key: "qty", label: "Jumlah", align: "end" },
        { key: "cost", label: "@HPP", align: "end" },
        { key: "total", label: "Total Nilai", align: "end" },
        ...(hasAnyAction
            ? [{ key: "actions", label: "Aksi", align: "end" }]
            : []),
    ];

    const renderCell = (item, columnKey) => {
        switch (columnKey) {
            case "no":
                return (
                    <ThemedText size="sm" variant="secondary">
                        {(table.pagination.page - 1) *
                            table.pagination.perPage +
                            table.items.indexOf(item) +
                            1}
                        .
                    </ThemedText>
                );
            case "date":
                return (
                    <ThemedText size="sm">{formatDate(item.date)}</ThemedText>
                );
            case "conversion":
                return (
                    <div className="flex items-center gap-2">
                        <ThemedText size="sm">
                            {item.source_material_name}
                        </ThemedText>
                        <LuArrowRight
                            size={14}
                            className="text-default-400 shrink-0"
                        />
                        <ThemedText
                            size="sm"
                            className="text-danger font-medium"
                        >
                            {item.dest_material_name}
                        </ThemedText>
                    </div>
                );
            case "warehouse":
                return (
                    <ThemedText size="sm">{item.warehouse_name}</ThemedText>
                );
            case "qty":
                return (
                    <ThemedText size="sm">
                        {formatThousands(item.quantity)} {item.unit_name}
                    </ThemedText>
                );
            case "cost":
                return (
                    <ThemedText size="sm" variant="secondary">
                        {formatCurrency(item.cost_price)}
                    </ThemedText>
                );
            case "total":
                return (
                    <ThemedText size="sm">
                        {formatCurrency(item.total_value)}
                    </ThemedText>
                );
            case "actions":
                return (
                    <div className="relative flex items-center justify-end gap-2">
                        {hasPermission("egg-conversion.edit") && (
                            <Tooltip content="Edit Konversi">
                                <Button
                                    isIconOnly
                                    size="sm"
                                    variant="light"
                                    as={Link}
                                    to={`/egg-conversion/${item.batch_id}/edit`}
                                >
                                    <LuPencil
                                        size={14}
                                        className="text-default-500"
                                    />
                                </Button>
                            </Tooltip>
                        )}
                        {hasPermission("egg-conversion.delete") && (
                            <Tooltip content="Hapus Konversi">
                                <Button
                                    isIconOnly
                                    size="sm"
                                    color="danger"
                                    variant="light"
                                    onPress={() =>
                                        table.deleteState.openDeleteModal(
                                            item.batch_id,
                                        )
                                    }
                                >
                                    <LuTrash size={14} />
                                </Button>
                            </Tooltip>
                        )}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="flex h-full w-full flex-col gap-4">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Konversi Telur"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Konversi Telur" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <FilterButton
                        isOpen={table.filterPanel.isOpen}
                        onToggle={() =>
                            table.filterPanel.setIsOpen(
                                !table.filterPanel.isOpen,
                            )
                        }
                        activeCount={table.filters.activeCount}
                    />
                    {hasPermission("egg-conversion.create") && (
                        <Button
                            as={Link}
                            to="/egg-conversion/create"
                            color="primary"
                            startContent={<LuPlus />}
                            size="sm"
                        >
                            Tambah Konversi
                        </Button>
                    )}
                </div>
            </div>

            <FilterData table={table} initialFilters={initialFilters} />

            <ActiveFilterChips
                filters={table.filters.values}
                filterLabels={filterLabels}
                onRemove={handleRemoveFilter}
            />

            <Card className="border-default-200 border" shadow="none">
                <CardHeader>
                    <DataTableControlBar table={table} />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            icon={LuEgg}
                            title={
                                table.filters.activeCount > 0
                                    ? "Data Tidak Ditemukan"
                                    : "Belum Ada Konversi"
                            }
                            description={
                                table.filters.activeCount > 0
                                    ? "Tidak ada konversi yang cocok dengan filter."
                                    : "Klik 'Tambah Konversi' untuk mencatat telur sehat yang pecah."
                            }
                        />
                    ) : (
                        <Table aria-label="Tabel Konversi Telur" shadow="none">
                            <TableHeader columns={columns}>
                                {(column) => (
                                    <TableColumn
                                        key={column.key}
                                        align={column.align}
                                        width={column.width}
                                    >
                                        {column.label}
                                    </TableColumn>
                                )}
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => (
                                    <TableRow key={item.batch_id}>
                                        {(columnKey) => (
                                            <TableCell>
                                                {renderCell(item, columnKey)}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>

            <DeleteConfirmModal
                table={table}
                title="Hapus Konversi Telur"
                description="Kedua baris stok (telur sehat & telur pecah) pada konversi ini akan dibatalkan. Jika stok telur pecah sudah terpakai, penghapusan akan ditolak."
            />
        </div>
    );
};

export default EggConversionData;
